import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { argomento_problema, ticket } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ODataClientError } from '@odata2ts/http-client-api'

export type TicketEditorData = {
  ticket: ticket
  problemArguments: argomento_problema[] | undefined
}

export const getTicketDetailQueryKey = (ticketRecordId?: number | null) => ['ticket-editor', ticketRecordId]

export const fetchAllProblemArguments = async (): Promise<argomento_problema[]> => {
  const qsAdminApi = useQsAdminApiManager.getState().service
  const response = await qsAdminApi.argomento_problema().query((builder, argomentoProblema) => {
    builder.expanding('qs_applicazione', (applicazioneBuilder, applicazione) => {
      applicazioneBuilder.select('id', 'nome')
    })
    builder.expanding('piattaforma', (piattaformaBuilder, piattaforma) => {
      piattaformaBuilder.select('id', 'nome')
    })
    builder.select('id', 'nome', 'note', 'qs_applicazione', 'piattaforma')
    builder.orderBy(argomentoProblema.ord.asc())
  })
  return response.data.value
}

export const fetchTicketEditorData = async (
  ticketRecordId: number,
  options: {
    clientId?: string
  } = {},
): Promise<TicketEditorData> => {
  const { clientId } = options
  const qsAdminApi = useQsAdminApiManager.getState().service

  const getTicket = await qsAdminApi
    .ticket(ticketRecordId)
    .query((builder, ticket) => {
      builder.expanding('tipologia_ticket', (tipologiaBuilder, tipologia) => {
        tipologiaBuilder.select('id', 'nome')
      })
      builder.expanding('assegnato', (assegnatoBuilder, assegnato) => {
        assegnatoBuilder.select('id', 'fullname', 'email')
      })
      builder.expanding('tipologia_problema', (tipologiaProblBuilder, tipologiaProbl) => {
        tipologiaProblBuilder.select('id', 'nome')
      })
      builder.expanding('argomento_problema', (argomentoProblBuilder, argomentoProbl) => {
        argomentoProblBuilder.select('id', 'nome')
      })
      builder.expanding('stato', (statoBuilder, stato) => {
        statoBuilder.select('id', 'nome')
      })
      builder.expanding('piattaforma', (piattaformaBuilder, piattaforma) => {
        piattaformaBuilder.select('id', 'nome')
      })
      builder.expanding('applicazione', (applicazioneBuilder, applicazione) => {
        applicazioneBuilder.select('id', 'nome')
      })
      builder.expanding('creatore', (creatoreBuilder, creatore) => {
        creatoreBuilder.select('id', 'fullname')
      })
      builder.expanding('incaricato', (incaricatoBuilder, incaricato) => {
        incaricatoBuilder.select('id', 'fullname', 'email')
      })
      builder.expanding('sede', (sedeBuilder, sede) => {
        sedeBuilder.expanding('azienda', (aziendaSedeBuilder, aziendaSede) => {
          aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
            sediAziendaSedeBuilder.expanding('assistenze', (assistenzeBuilder, assistenza) => {
              assistenzeBuilder.expanding('telefonica', (telefonicaBuilder, telefonica) => {
                telefonicaBuilder.select('id', 'nome')
              })
              assistenzeBuilder.select('id', 'sospesa', 'telefonica')
            })
            sediAziendaSedeBuilder.expanding('contatti_aziendali', (contattoBuilder, contatto) => {
              contattoBuilder.select('id', 'fullname', 'responsabile_tecnico')
            })
            sediAziendaSedeBuilder.select('id', 'nome', 'note', 'assistenze', 'contatti_aziendali')
          })
          aziendaSedeBuilder.select('id', 'nome', 'sedi')
        })
        sedeBuilder.expanding('assistenze', (assistenzeBuilder, assistenza) => {
          assistenzeBuilder.expanding('telefonica', (telefonicaBuilder, telefonica) => {
            telefonicaBuilder.select('id', 'nome')
          })
          assistenzeBuilder.select('id', 'sospesa', 'telefonica')
        })
        sedeBuilder.expanding('contatti_aziendali', (contattoBuilder, contatto) => {
          contattoBuilder.select('id', 'fullname', 'responsabile_tecnico')
        })
        sedeBuilder.select('id', 'nome', 'azienda', 'note', 'assistenze', 'contatti_aziendali')
      })
      builder.expanding('eventi', (eventiBuilder, evento) => {
        eventiBuilder.expanding('tipologia', (tipologiaEventoBuilder, tipologiaEvento) => {
          tipologiaEventoBuilder.select('id', 'nome')
        })
        eventiBuilder.expanding('utente', (utenteEventoBuilder, utenteEvento) => {
          utenteEventoBuilder.select('id', 'fullname')
        })
        eventiBuilder.orderBy(evento.data.desc())
      })
      builder.expanding('email_addizionali', (emailAziendaleBuilder, emailAziendale) => {
        emailAziendaleBuilder.select('id', 'email')
      })
      builder.expanding('dumps', (dumpsBuilder, dump) => {
        dumpsBuilder.expanding('licenza', (licenzaDumpBuilder, licenza) => {
          licenzaDumpBuilder.select('id', 'chiave')
        })
        dumpsBuilder.expanding('stato', (statoDumpBuilder, stato) => {
          statoDumpBuilder.select('id', 'nome')
        })
      })
    })
    .catch((error: ODataClientError) => {
      if (error.status === 404) throw new Error('404 Not Found: Ticket non trovato')
      throw new Error()
    })

  const currentTicket = getTicket.data satisfies ticket

  if (clientId && currentTicket?.sede?.azienda && currentTicket.sede.azienda.id !== Number(clientId)) {
    throw new Error(
      `Il task ${currentTicket.ded_Dis} appartiene al cliente ${currentTicket?.sede?.azienda.nome} e non al cliente corrente`,
    )
  }
  const problemArguments = await fetchAllProblemArguments()

  return { ticket: currentTicket, problemArguments }
}
